import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  listUsers,
  adjustBalance,
  updateRole,
  setDisabled,
  logoutAll,
  updatePermissions,
  deleteUser,
  createUser,
  resetPassword,
  changeOwnPassword,
  type CreateUserInput,
} from '@/api/users';
import type { Role } from '@/types';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => listUsers(),
  });
}

export function useAdjustBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; delta_seconds: number }) =>
      adjustBalance(args.id, args.delta_seconds),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; role: Role }) => updateRole(args.id, args.role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useSetDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; disabled: boolean }) => setDisabled(args.id, args.disabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useLogoutAll() {
  return useMutation({ mutationFn: (id: string) => logoutAll(id) });
}

export function useUpdatePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      id: string;
      perms: { can_delete_sessions?: boolean; can_delete_users?: boolean };
    }) => updatePermissions(args.id, args.perms),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (args: { id: string; new_password: string }) =>
      resetPassword(args.id, args.new_password),
  });
}

export function useChangeOwnPassword() {
  return useMutation({
    mutationFn: (args: { current_password: string; new_password: string }) =>
      changeOwnPassword(args.current_password, args.new_password),
  });
}
